import React from 'react';
import { get as _get } from 'lodash';
import { ProfileData } from '../../types';
import Icon from '../common/Icon';

interface ProfileHeaderProps {
  profile: ProfileData;
}

const getInitials = (companyName: string | null | undefined): string => {
  if (!companyName) return '?';
  return companyName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '?';
};

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ profile }) => {
  const [imageError, setImageError] = React.useState(false);
  const name = _get(profile, 'name') || 'Unknown';
  const title = _get(profile, 'title') || '';
  const companyName = _get(profile, 'company.name') || '';
  const archetype = _get(profile, 'archetype') || '';
  const email = _get(profile, 'email') || '';
  const phone = _get(profile, 'phone') || '';
  const location = _get(profile, 'location') || '';
  const bestContact = _get(profile, 'bestContact') || '';
  const linkedin = _get(profile, 'linkedin') || '';
  const avatarUrl = _get(profile, 'avatarUrl') || '';
  
  const initials = getInitials(name);
  return (
    <header className="bg-brand-black text-white rounded-brand p-10 flex flex-col md:flex-row items-center gap-10 relative overflow-hidden shadow-2xl mb-10 transition-all duration-700">
      <div className="absolute top-0 right-0 w-64 h-64 bg-brand-pink opacity-10 rounded-full -mr-32 -mt-32" />
      
      <div className="relative z-10">
        {((avatarUrl || "") !== "" && !imageError) ? (
        <img
          src={avatarUrl}
          alt={name}
          className="w-40 h-40 rounded-full border-4 border-brand-aqua object-cover shadow-2xl transition-transform hover:scale-105"
          onError={() => {
            setImageError(true);
          }}
        />
        ) : (
        <div className="w-40 h-40 bg-brand-blacks text-white flex items-center justify-center font-extrabold text-6xl rounded-full border-4 border-brand-aqua object-cover shadow-2xl transition-transform hover:scale-105">
          {initials}
        </div>
        )}
      </div>

      <div className="relative z-10 flex-1 text-center md:text-left">
        <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
          <h1 className="text-5xl font-extrabold tracking-tight leading-none">
            {name}
          </h1>
          {archetype && (
            <span className="bg-brand-aqua text-brand-black px-4 py-1 rounded-pill text-[10px] font-bold mb-2 uppercase tracking-widest">
              {archetype}
            </span>
          )}
        </div>

        {(title || companyName) && (
          <p className="text-brand-aqua text-xl font-medium mb-8">
            {title}{title && companyName && ' @ '}{companyName}
          </p>
        )}

        <div className="flex flex-col md:flex-row flex-wrap gap-x-12 gap-y-4 text-sm font-medium">
          {email && (
            <div className="flex items-center gap-2.5 text-brand-grey02">
              <Icon name="mail" className="text-brand-pink" />
              {email}
            </div>
          )}
          {phone && (
            <div className="flex items-center gap-2.5 text-brand-grey02">
              <Icon name="call" className="text-brand-pink" />
              {phone}
            </div>
          )}
          {location && (
            <div className="flex items-center gap-2.5 text-brand-grey02">
              <Icon name="public" className="text-brand-pink" />
              {location}
              {bestContact && (
                <>
                  <span className="text-brand-grey08 mx-2">|</span>
                  <span className="text-brand-aqua">Best: {bestContact}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {linkedin && (
        <div className="relative z-10 no-print">
          <a
            href={linkedin.startsWith('http') ? linkedin : `https://${linkedin}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-brand-grey09 border border-brand-grey08 px-6 py-3 rounded-pill hover:bg-brand-pink transition-all font-bold text-sm"
          >
            <Icon name="share" />
            LinkedIn
          </a>
        </div>
      )}
    </header>
  );
};

export default ProfileHeader;
